/**
 * Farmer Site — content-agnostic block renderer.
 *
 * A block is `{ id, kind, config, hidden, position }`. `content` is the
 * resolved-content bundle from `/api/farmers/websites/slug/{slug}`
 * (products, deals, surplus, journal, media, …). Themes never change
 * block behavior — only the visual tokens applied via CSS variables.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Leaf, Plant, Sun, Basket, MapPin, ClockCounterClockwise,
  Envelope, WhatsappLogo, Phone, ShieldCheck, Sparkle,
  Storefront, Camera, Newspaper, Confetti, ArrowRight, Tag,
} from "@phosphor-icons/react";

/* Utility: fresh-today window heuristic (72h). */
const FRESH_MS = 72 * 3600 * 1000;
const looksFresh = (p) => {
  if (p?.fresh_today) return true;
  const h = p?.harvest_date;
  if (!h) return false;
  const t = Date.parse(h.length > 10 ? h : `${h}T12:00:00Z`);
  return Number.isFinite(t) && (Date.now() - t) <= FRESH_MS;
};

const money = (v, ccy = "USD") => {
  if (v === undefined || v === null || v === "") return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: ccy || "USD", maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${v} ${ccy || ""}`;
  }
};

/* ------------------------------------------------------------------ */
/*  BLOCK: hero                                                        */
/* ------------------------------------------------------------------ */
function HeroBlock({ config, site }) {
  const headline = config.headline || site.tagline || `Welcome to ${site.farm_name}`;
  const subhead = config.subhead || site.short_description ||
    "Locally grown. Farmed with care. Delivered fresh to your door.";
  const image = config.image_url || site.hero_image_url ||
    "https://images.unsplash.com/photo-1518977676601-b53f82aba655?auto=format&fit=crop&w=1600&q=70";
  return (
    <section data-block="hero" className="relative w-full overflow-hidden"
      style={{ background: "var(--fs-panel)" }}>
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.28em]"
            style={{ color: "var(--fs-brand)" }}>
            <Sparkle size={11} weight="fill" />
            {(site.farm_types || []).slice(0, 2).join(" · ") || "Family Farm"}
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
            style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}
            data-testid="fs-hero-headline">
            {headline}
          </h1>
          <p className="mt-5 max-w-lg text-base md:text-lg" style={{ color: "var(--fs-ink)" }}>
            {subhead}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {config.cta_primary && (
              <a href={config.cta_primary_to || "#products"}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
                style={{ background: "var(--fs-brand)", color: "#fff" }}
                data-testid="fs-hero-cta-primary">
                {config.cta_primary} <ArrowRight size={14} weight="bold" />
              </a>
            )}
            {config.cta_secondary && (
              <a href={config.cta_secondary_to || "#contact"}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border"
                style={{ borderColor: "var(--fs-hairline)", color: "var(--fs-ink)" }}
                data-testid="fs-hero-cta-secondary">
                {config.cta_secondary}
              </a>
            )}
            {!config.cta_primary && (
              <a href="#products"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
                style={{ background: "var(--fs-brand)", color: "#fff" }}
                data-testid="fs-hero-cta-primary">
                Shop fresh <ArrowRight size={14} weight="bold" />
              </a>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-xl"
            style={{ boxShadow: "var(--fs-shadow)" }}>
            <img src={image} alt={site.farm_name}
              className="w-full h-full object-cover" loading="eager" />
          </div>
          {site.location?.city && (
            <div className="absolute -bottom-4 left-6 md:left-8 rounded-full px-4 py-2 text-xs shadow-md flex items-center gap-1.5"
              style={{ background: "var(--fs-panel)", color: "var(--fs-ink)" }}>
              <MapPin size={12} weight="fill" style={{ color: "var(--fs-brand)" }} />
              {[site.location.city, site.location.state, site.location.country].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: trust-badges                                                */
/* ------------------------------------------------------------------ */
function TrustBadgesBlock({ config }) {
  const badges = config.badges && config.badges.length
    ? config.badges
    : ["Fresh & Local", "Family Farmed", "Small Batch", "Community First"];
  const iconMap = [Leaf, ShieldCheck, Basket, Sparkle];
  return (
    <section data-block="trust-badges" className="border-y" style={{ borderColor: "var(--fs-hairline)" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {badges.slice(0, 4).map((b, i) => {
          const Icon = iconMap[i % iconMap.length];
          return (
            <div key={i} className="flex items-center gap-3" data-testid="fs-trust-badge">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: "var(--fs-brand)", color: "#fff" }}>
                <Icon size={16} weight="fill" />
              </div>
              <div className="text-sm font-medium" style={{ color: "var(--fs-ink)" }}>{b}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: fresh-today  (products with fresh_today or harvest_date<72h) */
/* ------------------------------------------------------------------ */
function FreshTodayBlock({ config, content }) {
  const items = (content?.fresh_today && content.fresh_today.length
    ? content.fresh_today
    : (content?.products || []).filter(looksFresh)
  ).slice(0, Math.max(1, Math.min(24, config.limit || 6)));
  if (items.length === 0) {
    return <EmptyBlock title={config.title || "Fresh today"} note="No fresh items right now — check back soon." />;
  }
  return (
    <SectionShell id="fresh-today" title={config.title || "Fresh today"} eyebrow="Picked in the last 72 hours">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {items.map((p) => <ProductCard key={p.id} p={p} freshBadge />)}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: products                                                    */
/* ------------------------------------------------------------------ */
function ProductsBlock({ config, content }) {
  let items = content?.products || [];
  if (config.category) items = items.filter((p) => p.category === config.category);
  items = items.slice(0, Math.max(1, Math.min(60, config.limit || 24)));
  if (items.length === 0) {
    return <EmptyBlock title={config.title || "Our produce"} note="Products will appear here once added." />;
  }
  return (
    <SectionShell id="products" title={config.title || "Our produce"} eyebrow="Shop">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {items.map((p) => <ProductCard key={p.id} p={p} />)}
      </div>
    </SectionShell>
  );
}

function BuyButton({ p }) {
  const [busy, setBusy] = useState(false);
  if (p.price == null || p.price <= 0) return null;
  const soldOut = p.quantity_available != null && p.quantity_available <= 0;
  const buy = async () => {
    if (busy || soldOut) return;
    setBusy(true);
    try {
      const { data } = await http.post("/farmers/shop/checkout", {
        product_id: p.id, quantity: 1,
        origin_url: window.location.origin, return_path: window.location.pathname,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout is unavailable right now");
      setBusy(false);
    }
  };
  return (
    <button onClick={buy} disabled={busy || soldOut} data-testid={`fs-buy-${p.id}`}
      className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-mono transition disabled:opacity-60"
      style={{ background: soldOut ? "#9CA3AF" : "var(--fs-brand)", color: "#fff" }}>
      <Basket size={13} weight="bold" /> {soldOut ? "Sold out" : busy ? "Opening…" : "Buy now"}
    </button>
  );
}

function ProductCard({ p, freshBadge }) {
  const image = (p.photos && p.photos[0]) ||
    "https://images.unsplash.com/photo-1571167530149-c1105da4c2c7?auto=format&fit=crop&w=800&q=70";
  return (
    <div className="group relative flex flex-col rounded-2xl overflow-hidden transition"
      style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}
      data-testid="fs-product-card">
      <div className="aspect-square overflow-hidden">
        <img src={image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500" loading="lazy" />
      </div>
      {freshBadge && (
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] font-mono rounded-full px-2 py-0.5"
          style={{ background: "var(--fs-brand)", color: "#fff" }}>
          Fresh today
        </div>
      )}
      {p.is_organic && (
        <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.22em] font-mono rounded-full px-2 py-0.5"
          style={{ background: "#166534", color: "#fff" }}>
          Organic
        </div>
      )}
      <div className="p-4 flex flex-col gap-1">
        <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: "var(--fs-muted)" }}>
          {p.category || "produce"}
        </div>
        <div className="text-base font-medium leading-tight" style={{ color: "var(--fs-ink)" }}>
          {p.name}
        </div>
        <div className="mt-1 flex items-center justify-between">
          <div className="text-sm font-semibold" style={{ color: "var(--fs-brand-ink)" }}>
            {money(p.price, p.currency) || "Ask for price"}
            {p.unit ? <span className="text-xs font-normal" style={{ color: "var(--fs-muted)" }}> / {p.unit}</span> : null}
          </div>
          {p.quantity_available != null && p.quantity_available <= 0 && (
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border"
              style={{ color: "#B91C1C", borderColor: "#B91C1C55", background: "#FEF2F2" }}
              data-testid={`fs-soldout-${p.id}`}>Sold out</span>
          )}
          {p.quantity_available != null && p.quantity_available > 0 && p.quantity_available <= 10 && (
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border animate-pulse"
              style={{ color: "#B45309", borderColor: "#B4530955", background: "#FFFBEB" }}
              data-testid={`fs-lowstock-${p.id}`}>Only {p.quantity_available} left</span>
          )}
        </div>
        <BuyButton p={p} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: featured-row  — deals + surplus                              */
/* ------------------------------------------------------------------ */
function FeaturedRowBlock({ config, content }) {
  const deals = (content?.deals || []).filter((d) => d.status !== "closed").slice(0, 3);
  const surplus = (content?.surplus || []).filter((s) => s.status !== "expired").slice(0, 3);
  const items = [
    ...deals.map((d) => ({ kind: "deal", it: d })),
    ...surplus.map((s) => ({ kind: "surplus", it: s })),
  ];
  if (items.length === 0) return null;
  return (
    <SectionShell id="deals" title={config.title || "Deals & surplus"} eyebrow="On offer this week">
      <div className="grid md:grid-cols-3 gap-5">
        {items.slice(0, 6).map(({ kind, it }, i) => (
          <div key={it.id || i} className="rounded-2xl p-5 flex flex-col justify-between"
            style={{ background: kind === "deal" ? "var(--fs-brand)" : "var(--fs-accent)", color: "#fff", boxShadow: "var(--fs-shadow)" }}
            data-testid={`fs-featured-${kind}`}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono opacity-80">
                {kind === "deal" ? "Deal" : "Surplus"}
              </div>
              <div className="mt-2 text-lg font-medium">{it.title}</div>
              {it.description && <p className="mt-2 text-sm opacity-90 line-clamp-3">{it.description}</p>}
            </div>
            <div className="mt-4 flex items-end justify-between text-sm">
              {kind === "deal" ? (
                <>
                  {it.new_price != null && <div className="text-2xl font-semibold">{money(it.new_price, it.currency)}</div>}
                  {it.discount_pct && <div className="opacity-90">−{it.discount_pct}%</div>}
                </>
              ) : (
                <>
                  {it.quantity ? <div className="text-lg font-medium">{it.quantity} {it.unit || ""}</div> : <div />}
                  {it.price != null && <div className="opacity-90">{money(it.price, it.currency)}</div>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: categories                                                  */
/* ------------------------------------------------------------------ */
function CategoriesBlock({ config, content }) {
  const cats = {};
  for (const p of (content?.products || [])) {
    if (!p.category) continue;
    cats[p.category] = (cats[p.category] || 0) + 1;
  }
  const rows = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 8);
  if (rows.length === 0) return null;
  return (
    <SectionShell id="categories" title={config.title || "Shop by category"} eyebrow="Browse">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {rows.map(([c, n]) => (
          <a key={c} href={`#products?cat=${c}`} className="rounded-xl px-3 py-4 text-center border transition"
            style={{ borderColor: "var(--fs-hairline)", background: "var(--fs-panel)" }}
            data-testid="fs-category-chip">
            <div className="text-lg font-medium capitalize" style={{ color: "var(--fs-ink)" }}>{c}</div>
            <div className="text-xs" style={{ color: "var(--fs-muted)" }}>{n} item{n !== 1 ? "s" : ""}</div>
          </a>
        ))}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: farm-story                                                  */
/* ------------------------------------------------------------------ */
function FarmStoryBlock({ config, site }) {
  const title = config.title || "Our story";
  const body = config.body || site.story || site.about ||
    "We started this farm to grow real food for real people. Every crop, every animal, every season is part of a story we're honored to share with you.";
  const image = config.image_url || site.hero_image_url ||
    "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=1200&q=70";
  return (
    <SectionShell id="story" eyebrow={config.eyebrow || "About the farm"}>
      <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="aspect-[4/3] rounded-3xl overflow-hidden" style={{ boxShadow: "var(--fs-shadow)" }}>
          <img src={image} alt={title} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl leading-tight tracking-tight"
            style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}>{title}</h2>
          <p className="mt-4 text-base md:text-lg leading-relaxed whitespace-pre-line"
            style={{ color: "var(--fs-ink)" }}>{body}</p>
          {config.cta_label && (
            <a href={config.cta_to || "#contact"}
              className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
              style={{ background: "var(--fs-ink)", color: "var(--fs-panel)" }}>
              {config.cta_label} <ArrowRight size={14} weight="bold" />
            </a>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: farm-info                                                   */
/* ------------------------------------------------------------------ */
function FarmInfoBlock({ config, site }) {
  const addr = [
    ...(site.location?.address_lines || []),
    [site.location?.village, site.location?.city].filter(Boolean).join(", "),
    [site.location?.state, site.location?.country].filter(Boolean).join(", "),
    site.location?.postal_code,
  ].filter(Boolean);
  const hours = config.hours || site.hours || {};
  return (
    <SectionShell id="farm-info" title={config.title || "Visit us"} eyebrow="How to find us">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="rounded-2xl p-6" style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}>
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--fs-ink)" }}>
            <MapPin size={16} weight="fill" style={{ color: "var(--fs-brand)" }} /> Address
          </div>
          <div className="mt-3 text-sm leading-relaxed" style={{ color: "var(--fs-ink)" }}>
            {addr.length ? addr.map((line, i) => <div key={i}>{line}</div>) : <span style={{ color: "var(--fs-muted)" }}>Address coming soon.</span>}
          </div>
        </div>
        <div className="rounded-2xl p-6" style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}>
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--fs-ink)" }}>
            <ClockCounterClockwise size={16} weight="fill" style={{ color: "var(--fs-brand)" }} /> Hours
          </div>
          <div className="mt-3 text-sm" style={{ color: "var(--fs-ink)" }}>
            {Object.keys(hours).length ? (
              Object.entries(hours).map(([day, val]) => (
                <div key={day} className="flex items-center justify-between border-b py-1.5" style={{ borderColor: "var(--fs-hairline)" }}>
                  <span className="capitalize">{day}</span>
                  <span style={{ color: "var(--fs-muted)" }}>{val}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "var(--fs-muted)" }}>Ask us for opening times.</div>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: journal                                                     */
/* ------------------------------------------------------------------ */
function JournalBlock({ config, content }) {
  const posts = (content?.journal || []).slice(0, Math.max(1, Math.min(24, config.limit || 6)));
  if (posts.length === 0) return null;
  return (
    <SectionShell id="journal" title={config.title || "From the farm journal"} eyebrow="Read">
      <div className="grid md:grid-cols-3 gap-6">
        {posts.map((p) => (
          <article key={p.id} className="rounded-2xl overflow-hidden flex flex-col"
            style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}
            data-testid="fs-journal-card">
            {p.cover_url && (
              <div className="aspect-[16/9] overflow-hidden">
                <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-5 flex-1">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: "var(--fs-brand)" }}>
                Journal · {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
              </div>
              <h3 className="mt-2 text-lg font-medium leading-snug" style={{ color: "var(--fs-ink)" }}>{p.title}</h3>
              {p.excerpt && <p className="mt-2 text-sm" style={{ color: "var(--fs-ink)" }}>{p.excerpt}</p>}
            </div>
          </article>
        ))}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: gallery                                                     */
/* ------------------------------------------------------------------ */
function GalleryBlock({ config, content }) {
  const media = (content?.media || []).slice(0, Math.max(1, Math.min(24, config.limit || 8)));
  if (media.length === 0) return null;
  return (
    <SectionShell id="gallery" title={config.title || "From the farm"} eyebrow="Photos">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {media.map((m) => (
          <div key={m.id} className="aspect-square overflow-hidden rounded-2xl" data-testid="fs-gallery-item">
            <img src={m.url} alt={m.caption || "Farm photo"} className="w-full h-full object-cover hover:scale-105 transition duration-500" loading="lazy" />
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: newsletter                                                  */
/* ------------------------------------------------------------------ */
function NewsletterBlock({ config, site }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle|sending|sent|error
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !site?.owner_uid) return;
    setState("sending");
    try {
      const { data } = await http.post("/farmers/shop/newsletter/subscribe", {
        owner_uid: site.owner_uid, email,
      });
      setState("sent");
      toast.success(data.already_subscribed ? "You're already on the list — thanks!" : "Thanks — you're subscribed!");
      setEmail("");
    } catch (err) {
      setState("error");
      toast.error(err?.response?.data?.detail || "Couldn't subscribe — try again.");
    }
  };
  return (
    <section data-block="newsletter" className="my-4">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="rounded-3xl p-8 md:p-12 grid md:grid-cols-[1.2fr,1fr] items-center gap-8"
          style={{ background: "var(--fs-brand)", color: "#fff", boxShadow: "var(--fs-shadow)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono opacity-80">
              <Envelope size={11} weight="fill" className="inline mr-1" /> Farm updates
            </div>
            <h3 className="mt-2 text-3xl md:text-4xl leading-tight" style={{ fontFamily: "var(--fs-display)" }}>
              {config.title || "Stay in touch with the farm"}
            </h3>
            <p className="mt-2 text-sm opacity-90 max-w-lg">
              {config.prompt || "Get seasonal updates, harvest calendars and first pick on limited harvests."}
            </p>
          </div>
          <form onSubmit={submit}
            className="flex gap-2 bg-white/10 rounded-full p-1.5 items-center">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={state === "sending"}
              className="flex-1 bg-transparent px-4 py-2 text-sm text-white placeholder-white/70 outline-none disabled:opacity-60" />
            <button type="submit" disabled={state === "sending" || !email}
              className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
              style={{ background: "#fff", color: "var(--fs-brand-ink)" }}
              data-testid="fs-newsletter-submit">
              {state === "sending" ? "…" : state === "sent" ? "Subscribed ✓" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: cta                                                         */
/* ------------------------------------------------------------------ */
function CTABlock({ config }) {
  return (
    <section data-block="cta" className="my-4">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="rounded-3xl p-8 md:p-12 text-center"
          style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}>
          <h3 className="text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}>
            {config.headline || "Come see us at the farm"}
          </h3>
          {config.subhead && <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: "var(--fs-muted)" }}>{config.subhead}</p>}
          {config.cta_label && (
            <a href={config.cta_to || "#contact"}
              className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
              style={{ background: "var(--fs-brand)", color: "#fff" }}
              data-testid="fs-cta-button">
              {config.cta_label} <ArrowRight size={14} weight="bold" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: contact                                                     */
/* ------------------------------------------------------------------ */
function ContactBlock({ config, site }) {
  const c = { ...(site.contact || {}), ...config };
  const hasAny = !!(c.email || c.phone || c.whatsapp);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !site?.owner_uid) return;
    setState("sending");
    try {
      await http.post("/farmers/shop/enquiries", {
        owner_uid: site.owner_uid,
        website_id: site.id,
        site_slug: site.slug,
        ...form,
      });
      setState("sent");
      toast.success("Thanks — we'll be in touch!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setState("error");
      toast.error(err?.response?.data?.detail || "Couldn't send — try again.");
    }
  };
  return (
    <SectionShell id="contact" title={config.title || "Get in touch"} eyebrow="Contact">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="rounded-2xl p-6 space-y-4"
          style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}>
          {c.email && (
            <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-sm" style={{ color: "var(--fs-ink)" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--fs-brand)", color: "#fff" }}>
                <Envelope size={14} weight="fill" />
              </div>
              {c.email}
            </a>
          )}
          {c.phone && (
            <a href={`tel:${c.phone}`} className="flex items-center gap-3 text-sm" style={{ color: "var(--fs-ink)" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--fs-brand)", color: "#fff" }}>
                <Phone size={14} weight="fill" />
              </div>
              {c.phone}
            </a>
          )}
          {c.whatsapp && (
            <a href={`https://wa.me/${String(c.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 text-sm" style={{ color: "var(--fs-ink)" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#25D366", color: "#fff" }}>
                <WhatsappLogo size={14} weight="fill" />
              </div>
              {c.whatsapp}
            </a>
          )}
          {!hasAny && (
            <div className="text-sm" style={{ color: "var(--fs-muted)" }}>
              Send the farmer a note using the form.
            </div>
          )}
        </div>
        {config.form_enabled !== false && (
          <form onSubmit={submit}
            className="rounded-2xl p-6 space-y-3"
            style={{ background: "var(--fs-panel)", boxShadow: "var(--fs-shadow)" }}>
            <input type="text" required placeholder="Your name"
              value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={state === "sending"}
              className="w-full rounded-lg border px-3 py-2 text-sm disabled:opacity-60" style={{ borderColor: "var(--fs-hairline)" }} />
            <input type="email" required placeholder="Your email"
              value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={state === "sending"}
              className="w-full rounded-lg border px-3 py-2 text-sm disabled:opacity-60" style={{ borderColor: "var(--fs-hairline)" }} />
            <textarea required placeholder="Message" rows={4}
              value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              disabled={state === "sending"}
              className="w-full rounded-lg border px-3 py-2 text-sm disabled:opacity-60" style={{ borderColor: "var(--fs-hairline)" }} />
            <button type="submit" disabled={state === "sending"}
              className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
              style={{ background: "var(--fs-brand)", color: "#fff" }}
              data-testid="fs-contact-submit">
              {state === "sending" ? "Sending…" : state === "sent" ? "Sent ✓" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: market-schedule (placeholder — real integration in Phase 2)  */
/* ------------------------------------------------------------------ */
function MarketScheduleBlock({ config }) {
  return (
    <SectionShell id="markets" title={config.title || "Find us at the market"} eyebrow="Schedule">
      <div className="rounded-2xl p-8 text-center border-2 border-dashed" style={{ borderColor: "var(--fs-hairline)" }}>
        <Storefront size={28} weight="fill" style={{ color: "var(--fs-brand)" }} className="mx-auto" />
        <p className="mt-3 text-sm" style={{ color: "var(--fs-muted)" }}>
          Farmer's market schedule coming soon. Configure this block in the site editor.
        </p>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  BLOCK: rich-text                                                   */
/* ------------------------------------------------------------------ */
function RichTextBlock({ config }) {
  if (!config.body) return null;
  return (
    <SectionShell id="text">
      <div className="max-w-3xl mx-auto text-base md:text-lg whitespace-pre-line leading-relaxed"
        style={{ color: "var(--fs-ink)" }}>
        {config.body}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared shells                                                      */
/* ------------------------------------------------------------------ */
function SectionShell({ id, title, eyebrow, children }) {
  return (
    <section id={id} data-block={id} className="py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {(title || eyebrow) && (
          <div className="mb-8 md:mb-10 flex flex-col gap-2">
            {eyebrow && (
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono"
                style={{ color: "var(--fs-brand)" }}>{eyebrow}</div>
            )}
            {title && (
              <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight"
                style={{ fontFamily: "var(--fs-display)", color: "var(--fs-ink)" }}>{title}</h2>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

function EmptyBlock({ title, note }) {
  return (
    <SectionShell title={title}>
      <div className="rounded-2xl p-10 text-center border-2 border-dashed"
        style={{ borderColor: "var(--fs-hairline)" }}>
        <Plant size={30} weight="fill" style={{ color: "var(--fs-muted)" }} className="mx-auto" />
        <p className="mt-3 text-sm" style={{ color: "var(--fs-muted)" }}>{note}</p>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Dispatcher                                                          */
/* ------------------------------------------------------------------ */
const RENDERERS = {
  "hero": HeroBlock,
  "trust-badges": TrustBadgesBlock,
  "fresh-today": FreshTodayBlock,
  "products": ProductsBlock,
  "featured-row": FeaturedRowBlock,
  "categories": CategoriesBlock,
  "farm-story": FarmStoryBlock,
  "farm-info": FarmInfoBlock,
  "journal": JournalBlock,
  "gallery": GalleryBlock,
  "newsletter": NewsletterBlock,
  "cta": CTABlock,
  "contact": ContactBlock,
  "market-schedule": MarketScheduleBlock,
  "rich-text": RichTextBlock,
};

export function renderBlock({ block, site, content }) {
  if (!block || block.hidden) return null;
  const Renderer = RENDERERS[block.kind];
  if (!Renderer) {
    return (
      <SectionShell title={block.kind}>
        <div className="text-sm" style={{ color: "var(--fs-muted)" }}>
          Block type &laquo;{block.kind}&raquo; not yet supported.
        </div>
      </SectionShell>
    );
  }
  return <Renderer config={block.config || {}} site={site} content={content || {}} />;
}

export const SUPPORTED_BLOCK_KINDS = Object.keys(RENDERERS);
